import React from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight, Calendar, Clock, MapPin, DollarSign, Users } from 'lucide-react';
import DurationPill from '../../../components/booking/DurationPill';
import LegendCard from '../../LandingPage/LegendCard';
import { getAvailabilityColor, getAvailabilityText } from '../../LandingPage/utils';
import { getDayOfWeek } from './utils';

// Border colour to pair with getAvailabilityColor()'s bg/text classes —
// mirrors the swatches in LegendCard so Step 1 visually matches the public
// Check Availability page exactly.
const availabilityBorderColor = (avail) => {
  if (!avail) return 'border-gray-200';
  if (avail.available_courts === 0) return 'border-red-300';
  const pct = (avail.available_courts / avail.total_courts) * 100;
  if (pct <= 40) return 'border-orange-300';
  return 'border-green-300';
};

export default function StepDateTime({
  // data
  locations, selectedLocation, selectedDate, selectedTimeSlot, duration,
  minDate, maxDate, timeSlots, slotPricing, slotAvailabilityMap, availability, courtsPricing,
  // derived
  breakInfo, operatingHoursDisplay, isLocationClosed,
  // helpers
  isTimeSlotPast, getConsecutiveSlots,
  // actions
  onLocationChange, onDateChange, onDurationChange,
  onTimeSlotClick, onNext,
}) {
  const dayName = selectedDate
    ? getDayOfWeek(selectedDate)?.replace(/^./, (c) => c.toUpperCase())
    : '';

  return (
    <div className="space-y-6 animate-slide-up" data-testid="step-datetime">
      <div>
        <h2 className="text-xl font-bold font-heading uppercase mb-4">Select Date & Time</h2>

        {/* Location */}
        <div className="mb-4">
          <Label className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
            <MapPin className="w-4 h-4 inline mr-1" />
            Location
          </Label>
          <Select value={selectedLocation?.id} onValueChange={onLocationChange}>
            <SelectTrigger data-testid="location-select" className="h-14 rounded-2xl">
              <SelectValue placeholder="Select a location" />
            </SelectTrigger>
            <SelectContent>
              {locations.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedLocation && (
            <p className="text-xs text-muted-foreground mt-1">{selectedLocation.address}</p>
          )}
        </div>

        {/* Date */}
        <div className="mb-4">
          <Label htmlFor="date" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
            <Calendar className="w-4 h-4 inline mr-1" />
            Date
            {dayName && (
              <span className="normal-case" data-testid="date-day-of-week">
                : {dayName}
              </span>
            )}
          </Label>
          <Input
            id="date"
            data-testid="date-input"
            type="date"
            value={selectedDate}
            onChange={(e) => onDateChange(e.target.value)}
            min={minDate}
            max={maxDate}
            className="h-14 rounded-2xl"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Bookings available up to 1 month in advance
          </p>
        </div>

        {/* Operating hours */}
        {selectedLocation && selectedDate && (
          <Card className="p-4 mb-4 bg-muted/50">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Operating Hours:</span>
              <span className={`text-sm font-bold ${isLocationClosed ? 'text-red-600' : 'text-green-600'}`}>
                {operatingHoursDisplay}
              </span>
            </div>
            {breakInfo && breakInfo.length > 0 && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-bold text-amber-800 flex items-center gap-2">
                  🕌 Blocked Hours
                </p>
                {breakInfo.map((b) => (
                  <p key={`${b.start}-${b.end}`} className="text-sm text-amber-700 mt-1">
                    {b.start} - {b.end}: {b.reason || 'Break'}
                  </p>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Duration */}
        <div className="mb-4">
          <Label className="uppercase text-xs font-bold text-muted-foreground mb-3 block">
            Session Duration
          </Label>
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3].map((hours) => (
              <button
                key={hours}
                data-testid={`duration-${hours}`}
                onClick={() => onDurationChange(hours)}
                className={`p-4 rounded-2xl border-2 transition-all font-bold ${
                  duration === hours
                    ? 'border-accent bg-accent/10 text-accent-foreground'
                    : 'border-border hover:border-primary'
                }`}
              >
                <Clock className="w-4 h-4 mx-auto mb-1" />
                {hours} {hours === 1 ? 'Hour' : 'Hours'}
              </button>
            ))}
          </div>
        </div>

        {/* Time slot grid */}
        <div>
          <Label className="uppercase text-xs font-bold text-muted-foreground mb-3 block">
            {duration > 1 ? `Select Starting Time (${duration} consecutive hours)` : 'Time Slot'}
          </Label>

          {isLocationClosed ? (
            <Card className="p-6 text-center bg-red-50 border-red-200">
              <p className="text-red-600 font-bold">Location Closed</p>
              <p className="text-sm text-red-500 mt-1">
                {selectedLocation?.name} is closed on this day. Please select a different date.
              </p>
            </Card>
          ) : timeSlots.length === 0 ? (
            <Card className="p-6 text-center">
              <p className="text-muted-foreground">No time slots available. Please select a location and date.</p>
            </Card>
          ) : (
            <>
              {duration > 1 && (
                <div className="mb-3 p-3 bg-blue-50 border-l-4 border-blue-500 text-sm">
                  <p className="font-bold text-blue-800">ℹ️ Multi-Hour Booking</p>
                  <p className="text-blue-700 mt-1">
                    Select your starting time. Your booking will automatically include {duration} consecutive hours.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                {timeSlots.map((slot) => {
                  const consecutive = duration > 1 ? getConsecutiveSlots(slot, duration) : [slot];
                  const isValidSelection = consecutive.length === duration;
                  const isSelected = selectedTimeSlot === slot;
                  const isHighlighted = duration > 1 && selectedTimeSlot
                    && getConsecutiveSlots(selectedTimeSlot, duration).includes(slot);
                  const isPast = isTimeSlotPast(slot);
                  const avail = slotAvailabilityMap?.[slot];
                  const isFullyBooked = isValidSelection && avail?.available_courts === 0;
                  const isDisabled = !isValidSelection || isPast;
                  const price = slotPricing[slot];

                  // Pre-compute slot styling so the JSX class block is a
                  // single expression instead of a 5-level nested ternary.
                  // Fully-booked slots stay CLICKABLE (leads to Join
                  // Waitlist below), same as the public Check Availability
                  // page — only past slots or "not enough consecutive
                  // hours left" slots are actually blocked from selection.
                  let slotClass;
                  if (isPast) {
                    slotClass = 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed opacity-50';
                  } else if (isSelected) {
                    slotClass = isFullyBooked
                      ? 'border-purple-400 bg-purple-50 text-purple-700 ring-2 ring-purple-400'
                      : 'border-accent bg-accent/20 text-accent-foreground ring-2 ring-accent';
                  } else if (isHighlighted) {
                    slotClass = 'border-accent/50 bg-accent/10 text-accent-foreground';
                  } else if (!isValidSelection) {
                    slotClass = 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed';
                  } else {
                    // Same colour scale as the public Check Availability page.
                    slotClass = `${availabilityBorderColor(avail)} ${getAvailabilityColor(avail)}`;
                  }

                  return (
                    <button
                      key={slot}
                      data-testid={`time-slot-${slot}`}
                      onClick={() => !isDisabled && onTimeSlotClick(slot)}
                      disabled={isDisabled}
                      className={`p-4 rounded-2xl border-2 transition-all text-left ${slotClass}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <Clock className="w-4 h-4 mb-1" />
                          <span className="font-bold">{slot}</span>
                        </div>
                        {price && !isPast && (
                          <span className="text-xs font-bold text-primary flex items-center gap-0.5">
                            <DollarSign className="w-3 h-3" />
                            {price.price_per_hour}/hr
                          </span>
                        )}
                      </div>
                      {isPast && <p className="text-xs mt-1">Passed</p>}
                      {!isPast && isValidSelection && isFullyBooked && (
                        <p className="text-xs mt-1 font-bold" data-testid={`slot-fully-booked-${slot}`}>
                          Fully Booked
                          {avail?.waitlist_count > 0 && (
                            <span className="block font-normal">
                              {avail.waitlist_count} waiting
                            </span>
                          )}
                        </p>
                      )}
                      {!isPast && isValidSelection && !isFullyBooked && avail && (
                        <p className="text-xs mt-1">{getAvailabilityText(avail)}</p>
                      )}
                      {!isPast && isSelected && duration > 1 && (
                        <p className="text-xs mt-1 text-accent font-bold">Start</p>
                      )}
                      {!isPast && isHighlighted && !isSelected && (
                        <p className="text-xs mt-1 text-accent">Included</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Availability + price summary */}
        {availability && selectedTimeSlot && (
          <Card className={`p-4 mt-4 ${availability.is_fully_booked ? 'bg-purple-50 border-purple-200' : 'glass-card'}`}>
            {duration > 1 && !availability.is_fully_booked && (
              <div className="mb-3 pb-3 border-b border-border/60">
                <DurationPill
                  duration={duration}
                  timeSlot={selectedTimeSlot}
                  variant="prominent"
                  testId="step-datetime"
                />
              </div>
            )}
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-bold uppercase text-muted-foreground mb-1">
                  {availability.is_fully_booked ? 'Fully Booked' : 'Available Courts'}
                </p>
                {availability.is_fully_booked ? (
                  <div>
                    <p className="text-2xl font-black font-heading text-purple-600">Join Waitlist</p>
                    <p className="text-xs text-purple-700 mt-1">
                      {availability.waitlist_count || 0}{' '}
                      {availability.waitlist_count === 1 ? 'person' : 'people'} waiting
                    </p>
                  </div>
                ) : (
                  <p className="text-2xl font-black font-heading">
                    {availability.available_courts} / {availability.total_courts}
                  </p>
                )}
              </div>
              {courtsPricing[0] && (
                <div className="text-right">
                  <p className="text-sm font-bold uppercase text-muted-foreground mb-1">Est. Price</p>
                  <p className="text-2xl font-black font-heading text-primary">
                    ${courtsPricing[0].price_per_hour}/hr
                  </p>
                </div>
              )}
            </div>
          </Card>
        )}

        <LegendCard />
      </div>

      <Button
        onClick={onNext}
        data-testid="datetime-next-button"
        className={`w-full h-14 ${availability?.is_fully_booked ? 'bg-purple-600 hover:bg-purple-700' : 'btn-volt'}`}
        disabled={!selectedDate || !selectedTimeSlot || isLocationClosed}
      >
        {availability?.is_fully_booked ? 'Join Waitlist' : 'Next'}
        {availability?.is_fully_booked
          ? <Users className="ml-2 w-5 h-5" />
          : <ArrowRight className="ml-2 w-5 h-5" />}
      </Button>
    </div>
  );
}
